import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PackageCarsSelectors } from "../store/selectors";
import { useCarsAvailabilityFilters } from "~core-cars/hooks";
import { CarsAvailabilityActions } from "~core-cars/store";

/**
 * Package-specific car availability filters hook that uses incremental pricing
 * instead of total charge for price filtering.
 */
export const usePackageCarsAvailabilityFilters = () => {
  const dispatch = useDispatch();
  const coreFiltersResult = useCarsAvailabilityFilters();

  // Override price filter options to use incremental pricing
  const packagePriceFilterOptions = useSelector(
    PackageCarsSelectors.getPackagePriceFilterOptions
  );

  // Override filtered cars to use incremental pricing
  const packageFilteredCars = useSelector(
    PackageCarsSelectors.getPackageFilteredCars
  );

  // Use package-specific filter detection
  const hasFilters = useSelector(PackageCarsSelectors.getPackageHasFilters);
  const totalFilterCount = useSelector(
    PackageCarsSelectors.getPackageTotalFilterCount
  );

  // Custom reset handler that resets to actual min/max from package data
  const resetPriceFilter = useCallback(() => {
    if (packagePriceFilterOptions) {
      dispatch(
        CarsAvailabilityActions.setPriceFilter({
          min: packagePriceFilterOptions.min.value,
          max: packagePriceFilterOptions.max.value,
        })
      );
    } else {
      // Fallback to core reset if no package options available
      dispatch(CarsAvailabilityActions.resetPriceFilter());
    }
  }, [dispatch, packagePriceFilterOptions]);

  const context = useMemo(
    () => ({
      ...coreFiltersResult.context,
      filterOptions: {
        ...coreFiltersResult.context.filterOptions,
        priceRange: packagePriceFilterOptions,
      },
      filteredCars: {
        data: {
          data: packageFilteredCars,
        },
        isLoading: coreFiltersResult.context.filteredCars.isLoading,
        isError: coreFiltersResult.context.filteredCars.isError,
      },
      hasFilters,
      totalFilterCount,
    }),
    [
      coreFiltersResult.context,
      packagePriceFilterOptions,
      packageFilteredCars,
      hasFilters,
      totalFilterCount,
    ]
  );

  const handlers = useMemo(
    () => ({
      ...coreFiltersResult.handlers,
      resetPriceFilter,
    }),
    [coreFiltersResult.handlers, resetPriceFilter]
  );

  return {
    context,
    handlers,
  };
};
